import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { Menu, MenuItem } from '@material-ui/core'
import moment from 'moment'
import { colors } from 'src/components/app-ui'
import { paymentStatus } from 'src/model/payment-status'

const AVATAR_COLORS = [
  ['#E9F1FF', '#0151D5'],
  ['#BFF1CE', '#1D9341'],
  ['#FEE2B3', '#B06E01'],
  ['#FAC6C7', '#A62E30'],
  ['#CEE6FF', '#4179B2'],
]

const GRID =
  'minmax(220px, 1.4fr) minmax(120px, 1fr) 100px 90px 90px minmax(170px, 1.2fr) 110px 32px'

const useStyles = makeStyles({
  scroll: {
    overflowX: 'auto',
  },
  head: {
    display: 'grid',
    gridTemplateColumns: GRID,
    minWidth: 1100,
    gap: '12px',
    padding: '10px 20px',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    color: colors.subtle,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: GRID,
    minWidth: 1100,
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    borderBottom: '1px solid #F1F3F5',
    fontSize: '14px',
  },
  learner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
  },
  avatar: {
    width: 32,
    height: 32,
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    fontSize: '12px',
    fontWeight: 600,
  },
  name: {
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  email: {
    fontSize: '12px',
    color: colors.subtle,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cell: {
    fontSize: '13px',
    color: colors.muted,
  },
  courseTitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '13px',
    fontWeight: 500,
  },
  courseStep: {
    marginTop: 1,
    fontSize: '12px',
    color: colors.subtle,
  },
  menuButton: {
    display: 'inline-flex',
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    color: colors.muted,
  },
  menuItem: {
    fontSize: '13px',
    color: colors.navy,
  },
  danger: {
    color: '#D4351C',
  },
})

const initialsOf = name =>
  (name || '?')
    .split(' ')
    .filter(Boolean)
    .map(word => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

const formatDate = value => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
}

/** "2 days ago", with the full date on hover. */
const LastActive = ({ value, className }) => {
  if (!value) return <span className={className}>—</span>
  const date = moment.utc(value).local()
  return (
    <span className={className} title={date.format('LLL')}>
      {date.fromNow()}
    </span>
  )
}

/**
 * The course the learner is on: its name and "Step n of N", or Completed once
 * they finished every course of the path.
 */
const CurrentCourse = ({ member }) => {
  const classes = useStyles()
  const total = member.totalCourses || 0
  const completed = member.completedCourses || 0
  const notStarted = (member.collectionStatus || '')
    .toLowerCase()
    .includes('not')

  let title
  let step
  if (!total) {
    title = '—'
    step = 'No courses'
  } else if (!member.currentCourseName && completed >= total) {
    title = 'Completed'
    step = `${completed} of ${total} courses`
  } else {
    title = member.currentCourseName || '—'
    step = `${notStarted ? 'Not started · ' : ''}Step ${
      member.currentCourseStep || 1
    } of ${total}`
  }

  return (
    <div style={{ minWidth: 0 }}>
      <div
        className={classes.courseTitle}
        style={{ color: title === 'Completed' ? '#1D9341' : colors.navy }}
        title={title}
      >
        {title}
      </div>
      <div className={classes.courseStep}>{step}</div>
    </div>
  )
}

// MUI Menu renders in a portal, so the scrolling table doesn't clip it.
const RowMenu = ({ onEdit, onDelete }) => {
  const classes = useStyles()
  const [anchor, setAnchor] = useState(null)
  const close = () => setAnchor(null)

  return (
    <>
      <button
        type="button"
        title="More"
        className={classes.menuButton}
        onClick={event => setAnchor(event.currentTarget)}
      >
        <Icon icon="tabler:dots-vertical" width={16} height={16} />
      </button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        <MenuItem
          className={classes.menuItem}
          onClick={() => {
            close()
            onEdit()
          }}
        >
          Edit learner
        </MenuItem>
        <MenuItem
          className={`${classes.menuItem} ${classes.danger}`}
          onClick={() => {
            close()
            onDelete()
          }}
        >
          Delete learner
        </MenuItem>
      </Menu>
    </>
  )
}

/**
 * Enrolled learners table.
 *
 * TODO(backend): learners have no tags yet; the Tags cells show "—".
 */
const MembersTable = ({ members, onEdit, onDelete }) => {
  const classes = useStyles()

  return (
    <div className={classes.scroll}>
      <div className={classes.head}>
        <span>Learner</span>
        <span>Tags</span>
        <span>Added</span>
        <span>Payment</span>
        <span>Certificate</span>
        <span>Current course</span>
        <span>Last active</span>
        <span />
      </div>
      {members.map((member, index) => {
        const [background, color] = AVATAR_COLORS[index % AVATAR_COLORS.length]
        const payment =
          paymentStatus.find(item => item.id === member.paymentStatus)?.name ||
          'Free'
        return (
          <div key={member.id} className={classes.row}>
            <div className={classes.learner}>
              <span className={classes.avatar} style={{ background, color }}>
                {initialsOf(member.name || member.email)}
              </span>
              <div style={{ minWidth: 0 }}>
                <div className={classes.name}>{member.name || '—'}</div>
                <div className={classes.email}>{member.email}</div>
              </div>
            </div>
            <span className={classes.cell}>—</span>
            <span className={classes.cell}>
              {formatDate(member.membershipAddedAt)}
            </span>
            <span className={classes.cell}>{payment}</span>
            <span
              className={classes.cell}
              style={member.certificate ? { color: '#1D9341' } : undefined}
            >
              {member.certificate ? 'Yes' : 'No'}
            </span>
            <CurrentCourse member={member} />
            <LastActive
              value={member.lastActivityAt}
              className={classes.cell}
            />
            <RowMenu
              onEdit={() => onEdit(member)}
              onDelete={() => onDelete(member)}
            />
          </div>
        )
      })}
    </div>
  )
}

export default MembersTable
